import { css, html } from 'lit'
import { customElement, property, query, queryAssignedElements } from 'lit/decorators.js'
import { ifDefined } from 'lit/directives/if-defined.js'
import { SwatchRGB, accentBaseColor } from '@fluentui/web-components'
import type { ButtonAppearance, Swatch } from '@fluentui/web-components'
import type { CSSDesignToken } from '@microsoft/fast-foundation'
import { LitHauntedElement } from '../mixins/lit-haunted-element'
import type { Property } from 'csstype'
import { useEffect } from 'haunted'
import chroma from 'chroma-js'


/**
 * Wrapper for <fluent-button> that adds some extra features
 */
@customElement('ep-fluent-button')
export class EPFluentButton extends LitHauntedElement {
  @property({ reflect: true })
  appearance: ButtonAppearance = 'neutral'

  @property({ type: Boolean, reflect: true })
  disabled: boolean = false;

  @property({ attribute: 'accent-color', reflect: true })
  accentColor?: Property.Color;

  @query('fluent-button')
  fluentButton?: HTMLElement;

  @queryAssignedElements({ slot: 'start', flatten: true })
  slottedStart?: HTMLInputElement[];
  @queryAssignedElements({ slot: 'end', flatten: true })
  slottedEnd?: HTMLInputElement[];

  get startSlot(): string | undefined {
    return this.slottedStart && this.slottedStart.length > 0 ? 'start' : undefined;
  }
  get endSlot(): string | undefined {
    return this.slottedEnd && this.slottedEnd.length > 0 ? 'end' : undefined;
  }

  render() {
    useEffect(() => this._handleColorChange(this.accentColor, accentBaseColor), [this.accentColor])

    return html`
      <fluent-button appearance=${this.appearance} ?disabled=${this.disabled}>
        <slot name="start" slot=${ifDefined(this.startSlot)} @slotchange=${() => this.requestUpdate()}></slot>
        <slot></slot>
        <slot name="end" slot=${ifDefined(this.endSlot)} @slotchange=${() => this.requestUpdate()}></slot>
      </fluent-button>
    `
  }

  private _handleColorChange(color: Property.Color | undefined, token: CSSDesignToken<Swatch>) {
    if (this.fluentButton && color) {
      // Get original color for debugging
      const original = token.getValueFor(this.fluentButton);

      // Map CSS color to swatch representation
      const [r, g, b] = chroma(color).rgb();
      const swatch = SwatchRGB.create(r / 255, g / 255, b / 255);

      // Replace the swatch used for this token
      token.setValueFor(this.fluentButton, swatch);

      // Debug message
      console.debug(`[${this.tagName}] Color Token "${token.name}" overriden from %c Color %c to %c Color %c`,
        `background: ${original.toColorString()}; color: #fff;`,
        'all: unset;',
        `background: rgb(${r},${g},${b}); color: #fff;`,
        'all: unset;',
        );
    }
  }

  static styles = css`
    :host {
      /* display: contents; */
      display: inline-flex;
    }

    fluent-button::part(content) {
      display: inline-flex;
    }

    fluent-button::part(control) {
      /*
        The default here is what was set by <fluent-button>. If possible, we don't want to hard-code it here.
        Too bad there isn't a better way in CSS to overwrite a property only if a variable is defined.
      */
      padding: var(--inner-padding, 0 calc((10 + (var(--design-unit) * 2 * var(--density))) * 1px));
    }
  `
}

declare global {
  interface HTMLElementTagNameMap {
    'ep-fluent-button': EPFluentButton
  }
}
